import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import {
  partnerDashboardService,
  PartnerAgent,
  CreatePartnerAgentDto,
  UpdatePartnerAgentDto,
} from "../../../apis/partnerDashboard";

interface PartnerAgentsProps {
  partnerId: string;
}

const PartnerAgents: React.FC<PartnerAgentsProps> = ({ partnerId }) => {
  const [agents, setAgents] = useState<PartnerAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<PartnerAgent | null>(null);
  const [formData, setFormData] = useState<CreatePartnerAgentDto & { password?: string }>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    canViewTransactions: false,
    canViewCars: true,
    canViewDrivers: true,
    canViewOwners: true,
    canViewMap: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchAgents();
  }, [partnerId]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const data = await partnerDashboardService.getPartnerAgents(partnerId);
      console.log("Partner Agents API Response:", data);
      setAgents(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error("Error fetching agents:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch agents",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
      canViewTransactions: false,
      canViewCars: true,
      canViewDrivers: true,
      canViewOwners: true,
      canViewMap: true,
    });
    setOpenCreateModal(true);
  };

  const handleEditAgent = (agent: PartnerAgent) => {
    setSelectedAgent(agent);
    setFormData({
      firstName: agent.firstName,
      lastName: agent.lastName,
      email: agent.email,
      phone: agent.phone,
      password: "", // Not required for edit, but keep it in state
      canViewTransactions: agent.permissions.canViewTransactions,
      canViewCars: agent.permissions.canViewCars,
      canViewDrivers: agent.permissions.canViewDrivers,
      canViewOwners: agent.permissions.canViewOwners,
      canViewMap: agent.permissions.canViewMap,
    });
    setOpenEditModal(true);
  };

  const handleDeleteAgent = (agent: PartnerAgent) => {
    setSelectedAgent(agent);
    setOpenDeleteDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    if (openCreateModal && !formData.password) {
      setSnackbar({
        open: true,
        message: "Password is required",
        severity: "error",
      });
      return;
    }

    try {
      if (openCreateModal) {
        // Type assertion: password is guaranteed to exist due to validation above
        await partnerDashboardService.createPartnerAgent(partnerId, formData as CreatePartnerAgentDto);
        setSnackbar({
          open: true,
          message: "Agent created successfully. Login credentials have been sent via email.",
          severity: "success",
        });
      } else if (selectedAgent) {
        const updateData: UpdatePartnerAgentDto = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          canViewTransactions: formData.canViewTransactions,
          canViewCars: formData.canViewCars,
          canViewDrivers: formData.canViewDrivers,
          canViewOwners: formData.canViewOwners,
          canViewMap: formData.canViewMap,
        };
        await partnerDashboardService.updatePartnerAgent(partnerId, selectedAgent._id, updateData);
        setSnackbar({
          open: true,
          message: "Agent updated successfully",
          severity: "success",
        });
      }
      setOpenCreateModal(false);
      setOpenEditModal(false);
      setSelectedAgent(null);
      fetchAgents();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to save agent",
        severity: "error",
      });
    }
  };

  const confirmDelete = async () => {
    if (!selectedAgent) return;
    try {
      await partnerDashboardService.deletePartnerAgent(partnerId, selectedAgent._id);
      setSnackbar({
        open: true,
        message: "Agent deleted successfully",
        severity: "success",
      });
      setOpenDeleteDialog(false);
      setSelectedAgent(null);
      fetchAgents();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to delete agent",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Partner Agents</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateAgent} startIcon={<Add />}>
          Add Agent
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No agents found
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent._id}>
                  <TableCell>{`${agent.firstName} ${agent.lastName}`}</TableCell>
                  <TableCell>{agent.email}</TableCell>
                  <TableCell>{agent.phone}</TableCell>
                  <TableCell>
                    <Chip
                      label={agent.status}
                      color={agent.status === "active" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {agent.permissions.canViewTransactions && (
                        <Chip label="Transactions" size="small" variant="outlined" />
                      )}
                      {agent.permissions.canViewCars && <Chip label="Cars" size="small" variant="outlined" />}
                      {agent.permissions.canViewDrivers && (
                        <Chip label="Drivers" size="small" variant="outlined" />
                      )}
                      {agent.permissions.canViewOwners && (
                        <Chip label="Owners" size="small" variant="outlined" />
                      )}
                      {agent.permissions.canViewMap && <Chip label="Map" size="small" variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditAgent(agent)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteAgent(agent)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog
        open={openCreateModal || openEditModal}
        onClose={() => {
          setOpenCreateModal(false);
          setOpenEditModal(false);
          setSelectedAgent(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{openCreateModal ? "Create Agent" : "Edit Agent"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              required
            />
            {openCreateModal && (
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                fullWidth
                required
              />
            )}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Permissions
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canViewTransactions}
                  onChange={(e) => setFormData({ ...formData, canViewTransactions: e.target.checked })}
                />
              }
              label="View Transactions"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canViewCars}
                  onChange={(e) => setFormData({ ...formData, canViewCars: e.target.checked })}
                />
              }
              label="View Cars"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canViewDrivers}
                  onChange={(e) => setFormData({ ...formData, canViewDrivers: e.target.checked })}
                />
              }
              label="View Drivers"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canViewOwners}
                  onChange={(e) => setFormData({ ...formData, canViewOwners: e.target.checked })}
                />
              }
              label="View Owners"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.canViewMap}
                  onChange={(e) => setFormData({ ...formData, canViewMap: e.target.checked })}
                />
              }
              label="View Map"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreateModal(false);
              setOpenEditModal(false);
              setSelectedAgent(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {openCreateModal ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedAgent?.firstName} {selectedAgent?.lastName}? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerAgents;

